import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Autocomplete, Checkbox, Container, DataGrid, Divider, Spacer, Typography } from '@emburse/embark-core';
import { getDepartments } from '../../../../api/api';
import { Employee } from '../../../../interfaces/Employee';
import DataGridLoading from '../../../../views/QuickSetup/components/DataGridLoading';
import { generateRandomGUID } from '../../../../util/helpers';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
const defaultData = {
    Depts: [],
    ExpCats: []
};
const CategoryFiltration = forwardRef(({ data, loading }, ref) => {
    useTranslation();
    const [tableData, setTableData] = useState(defaultData);
    const [departments, setDepartments] = useState([]);
    useEffect(() => {
        if (data) {
            const updatedData = JSON.parse(JSON.stringify(data));
            const updatedDepts = updatedData.Depts.map((item) => ({
                ...item,
                id: generateRandomGUID()
            }));
            updatedData.Depts = updatedDepts;
            setTableData(updatedData);
        }
        else {
            setTableData(defaultData);
        }
    }, [data]);
    useEffect(() => {
        if (!departments.length) {
            getDepartments({ Emp: new Employee() }).then(setDepartments);
        }
    }, [departments]);
    useImperativeHandle(ref, () => ({
        getData() {
            return tableData;
        }
    }));
    const updateGlobalExpCats = (e, value, reason, details) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        const updatedExpCats = tableDataCopy.ExpCats.map((expcat) => expcat.PK === details.option.PK
            ? { ...expcat, Filtered: reason === 'removeOption' }
            : expcat);
        tableDataCopy.ExpCats = updatedExpCats;
        setTableData(tableDataCopy);
    };
    const updateExpCatsForRow = (e, cell, value, reason, details) => {
        const deptCopy = JSON.parse(JSON.stringify(cell.row));
        const updatedExpCats = deptCopy.ExpCats.map((expcat) => expcat.PK === details.option.PK
            ? { ...expcat, Filtered: reason === 'selectOption' }
            : expcat);
        deptCopy.ExpCats = updatedExpCats;
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        const updatedDepts = tableDataCopy.Depts.map((dept) => dept.id === cell.id ? deptCopy : dept);
        tableDataCopy.Depts = updatedDepts;
        setTableData(tableDataCopy);
    };
    const updateDeptFiltering = (e, cell) => {
        const deptCopy = JSON.parse(JSON.stringify(cell.row));
        deptCopy.Filtering = !deptCopy.Filtering;
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        const updatedDepts = tableDataCopy.Depts.map((dept) => dept.id === cell.id ? deptCopy : dept);
        tableDataCopy.Depts = updatedDepts;
        setTableData(tableDataCopy);
    };
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(Typography, { variant: "h4", children: $t('Department Filtration of Categories') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`In Certify, you can choose whether members of a certain department are able to select a given 
                expense category while creating and submitting their expense reports. Remember - expense categories 
                can change the kind of data a submitter needs to populate.`) }), _jsx(Spacer, { size: 45 }), _jsx(Typography, { variant: "h5", children: $t('Global Categories') }), _jsx(Spacer, { size: 25 }), _jsx(Typography, { children: $t(`Populate the expense categories that you would like all members of your organization to use. 
                For example, if your company policy allows all employees to reimburse flights, add the Airfare category to this section.`) }), _jsx(Spacer, { size: 25 }), _jsx(Autocomplete, { id: "GlobalExpCats", multiple: true, multipleType: "chips", label: $t('Global Expense Categories'), style: { maxWidth: '600px' }, limitTags: 4, disableCloseOnSelect: true, disableClearable: true, showCheckbox: true, disabled: loading, value: tableData.ExpCats.filter((value) => !value.Filtered), options: tableData.ExpCats, onChange: (e, value, reason, details) => updateGlobalExpCats(e, value, reason, details), getOptionLabel: (option) => option.Name, isOptionEqualToValue: (option, value) => option.PK === value.PK }), _jsx(Spacer, { size: 35 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), _jsx(Spacer, { size: 25 }), _jsx(Typography, { variant: "h5", children: $t('Filtered Categories') }), _jsx(Spacer, { size: 25 }), _jsx(Typography, { children: $t('In this section, you can select categories that should only be seen by members of a specific department.') }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`For example, if only the Sales department is authorized to expense meals for potential clients, then assign 
                the Meals category to the Sales department, and ensure that it isn't in the Global Categories list above.`) }), _jsx(Spacer, { size: 10 }), _jsxs(Typography, { children: [$t('Selecting the "Strict Filtering" checkbox means that members of the given department can'), _jsx("strong", { children: $t(' only ') }), $t('see the categories in the box on the right - and not the categories in the Global Categories list.')] }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`After setting up filtration rules, you might notice that additional categories have been added to your account. 
                Certify creates these to make your filtration work as intended.`) }), _jsx(Spacer, { size: 25 }), _jsx(DataGrid, { autoHeight: true, pageSizeOptions: [5, 10, 100], localeText: { noRowsLabel: '' }, rows: tableData.Depts, columns: [
                    {
                        field: 'Filtering',
                        headerName: $t('Strict Filtering'),
                        sortable: false,
                        width: 110,
                        renderCell: (cell) => (_jsx("div", { style: {
                                width: '100%',
                                textAlign: 'center'
                            }, children: _jsx(Checkbox, { id: "formPayrollExpertsRun", color: "primary", checked: cell.value, onChange: (e) => updateDeptFiltering(e, cell) }) }))
                    },
                    {
                        field: 'Name',
                        headerName: $t('Department'),
                        flex: 4
                    },
                    {
                        field: 'ExpCats',
                        headerName: $t('Categories'),
                        flex: 6,
                        renderCell: (cell) => (_jsx(Autocomplete, { id: "ExpCats", multiple: true, multipleType: "chips", label: $t('Expense Categories'), style: { width: '100%', minWidth: 'auto' }, limitTags: 4, disableCloseOnSelect: true, disableClearable: true, showCheckbox: true, value: cell.value.filter((value) => value.Filtered), options: cell.value, onChange: (e, value, reason, details) => updateExpCatsForRow(e, cell, value, reason, details), getOptionLabel: (option) => option.Name, isOptionEqualToValue: (option, value) => option.PK === value.PK }))
                    }
                ] }), loading && _jsx(DataGridLoading, {}), _jsx(Spacer, { size: 35 })] }));
});
export default CategoryFiltration;
